import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  RefreshCw, Plus, Search, Pause, Play, X, Trash2, GitBranch,
  ExternalLink, AlertTriangle, CheckCircle2, Circle, MessageSquare, RotateCcw
} from "lucide-react";
import {
  getOpsBoard, getOpsCard, createOpsCard, updateOpsCard, moveOpsCard,
  commentOpsCard, deleteOpsCard, setOpsBoardPaused, sweepOpsLeases
} from "../services/api";
import {
  OPS_COLUMN_ORDER, OPS_COLUMN_LABELS, OPS_COLUMN_HINTS, priorityBand,
  type OpsCard, type OpsColumn, type OpsEvent, type OpsStream, type AcceptanceCriterion
} from "../types/board";
import { useToast } from "../components/ToastProvider";
import { ConfirmDialog } from "../components/ConfirmDialog";
import { usePageTitle } from "../hooks/usePageTitle";

const POLL_MS = 45_000;

function shortDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleDateString("en-ZA", { day: "numeric", month: "short" });
}
function fullDate(iso: string): string {
  const d = new Date(iso);
  return d.toLocaleString("en-ZA", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}
function relative(iso: string): string {
  const mins = Math.round((Date.now() - new Date(iso).getTime()) / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const h = Math.round(mins / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
}

export function BoardPage() {
  usePageTitle("Board");
  const toast = useToast();

  const [streams, setStreams] = useState<OpsStream[]>([]);
  const [cards, setCards] = useState<OpsCard[]>([]);
  const [paused, setPaused] = useState(true);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [stream, setStream] = useState<string>("all");
  const [q, setQ] = useState("");
  const [openRef, setOpenRef] = useState<string | null>(null);
  const [draggingRef, setDraggingRef] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  // A poll issued before a drag can land after it and put the card back. Any
  // poll older than the last local mutation is discarded.
  const mutationAt = useRef(0);

  const load = useCallback(async (quiet?: boolean) => {
    if (!quiet) setLoading(true);
    if (!quiet) setError(null);
    const issuedAt = Date.now();
    try {
      const res = await getOpsBoard();
      if (issuedAt < mutationAt.current) return; // superseded by a local change
      setStreams(res.streams ?? []);
      setCards(res.cards ?? []);
      setPaused(!!res.meta?.paused);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load the board.");
    } finally {
      if (!quiet) setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  // Refresh in the background so an agent moving a card shows up without a click.
  useEffect(() => {
    const t = setInterval(() => {
      if (document.hidden) return;
      void load(true);
    }, POLL_MS);
    const onVisible = () => { if (!document.hidden) void load(true); };
    document.addEventListener("visibilitychange", onVisible);
    return () => { clearInterval(t); document.removeEventListener("visibilitychange", onVisible); };
  }, [load]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return cards.filter((c) => {
      if (stream !== "all" && c.streamId !== stream) return false;
      if (!needle) return true;
      return (
        c.title.toLowerCase().includes(needle) ||
        c.ref.toLowerCase().includes(needle) ||
        c.body.toLowerCase().includes(needle) ||
        c.labels.some((l) => l.toLowerCase().includes(needle))
      );
    });
  }, [cards, stream, q]);

  const byColumn = useMemo(() => {
    const map = {} as Record<OpsColumn, OpsCard[]>;
    const unknown: OpsCard[] = [];
    for (const col of OPS_COLUMN_ORDER) map[col] = [];
    // A card in a column this build does not know about used to be dropped on
    // the floor while still counting toward the tab total. Surface it instead.
    for (const c of visible) (map[c.col] ? map[c.col] : unknown).push(c);
    if (unknown.length) map[OPS_COLUMN_ORDER[0]] = [...unknown, ...map[OPS_COLUMN_ORDER[0]]];
    for (const col of OPS_COLUMN_ORDER) {
      map[col].sort((a, b) => a.priority - b.priority || a.createdAt.localeCompare(b.createdAt));
    }
    return map;
  }, [visible]);

  const streamName = useCallback(
    (id: string) => streams.find((s) => s.id === id)?.name ?? id,
    [streams]
  );
  const streamColour = useCallback(
    (id: string) => streams.find((s) => s.id === id)?.colour ?? "var(--accent)",
    [streams]
  );

  /** Optimistic: the card lands in the new column immediately, reverts on failure. */
  const move = useCallback(async (ref: string, to: OpsColumn) => {
    const current = cards.find((c) => c.ref === ref);
    if (!current || current.col === to) return;
    mutationAt.current = Date.now();
    const from = current.col;
    setCards((prev) => prev.map((c) => (c.ref === ref ? { ...c, col: to } : c)));
    try {
      // viaUi marks this as a human on the board. SHIPPED means production, and
      // it is closed to every other caller.
      const res = await moveOpsCard(ref, to, undefined, true);
      setCards((prev) => prev.map((c) => (c.ref === ref ? res.card : c)));
    } catch (err) {
      setCards((prev) => prev.map((c) => (c.ref === ref ? { ...c, col: from } : c)));
      toast.push("error", err instanceof Error ? err.message : "Could not move the card.");
    }
  }, [cards, toast]);

  const togglePause = useCallback(async () => {
    const next = !paused;
    setPaused(next);
    try {
      await setOpsBoardPaused(next);
      toast.push("success", next ? "Fleet paused. Nothing will claim a ticket." : "Fleet resumed.");
    } catch (err) {
      setPaused(!next);
      toast.push("error", err instanceof Error ? err.message : "Could not change the kill switch.");
    }
  }, [paused, toast]);

  const sweep = useCallback(async () => {
    try {
      const res = await sweepOpsLeases();
      if (res.recovered.length) {
        toast.push("success", `Returned ${res.recovered.length} stranded ticket${res.recovered.length === 1 ? "" : "s"} to Ready.`);
        void load(true);
      } else {
        toast.push("info", "No stranded tickets. Every claim is live.");
      }
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Sweep failed.");
    }
  }, [toast, load]);

  // Counted from the whole board, not the filtered view: the copy says "cards
  // need a decision from you", which does not change because you typed in the
  // search box.
  const needsYou = cards.filter((c) => c.col === "needs_jared").length;

  return (
    <div className="page">
      <header className="page-header">
        <div>
          <span className="eyebrow">Operations</span>
          <h1>Board</h1>
          <p className="muted">
            The work queue for every Orbit stream. Cards move left to right; only you move anything into Shipped.
          </p>
        </div>
        <div className="page-actions">
          <button className="btn btn-ghost" onClick={sweep} title="Return tickets stranded by a dead worker">
            <RotateCcw size={14} /> Sweep
          </button>
          <button className={paused ? "btn btn-ghost" : "btn btn-danger"} onClick={() => void togglePause()}>
            {paused ? <Play size={14} /> : <Pause size={14} />} {paused ? "Fleet paused" : "Pause fleet"}
          </button>
          <button className="btn btn-ghost" onClick={() => void load()} disabled={loading}>
            <RefreshCw size={14} className={loading ? "spin" : undefined} /> Refresh
          </button>
          <button className="btn btn-primary" onClick={() => setComposing(true)}>
            <Plus size={14} /> New card
          </button>
        </div>
      </header>

      {error ? (
        <div className="card" style={{ borderColor: "rgba(255,92,122,0.3)" }}>
          <p style={{ color: "var(--danger)", margin: 0 }}>{error}</p>
        </div>
      ) : null}

      {paused ? (
        <div className="board-banner">
          <Pause size={15} />
          <span>
            <strong>Fleet paused.</strong> Nothing will claim a ticket. The board is yours to use by hand in
            the meantime.
          </span>
        </div>
      ) : null}

      {needsYou > 0 ? (
        <div className="board-banner warn">
          <AlertTriangle size={15} />
          <span>
            <strong>{needsYou}</strong> card{needsYou === 1 ? "" : "s"} need a decision from you.
          </span>
        </div>
      ) : null}

      <div className="board-controls">
        <div className="tabs">
          <button className={"tab" + (stream === "all" ? " active" : "")} onClick={() => setStream("all")}>
            All <span className="board-tab-count">{cards.length}</span>
          </button>
          {streams.map((s) => (
            <button
              key={s.id}
              className={"tab" + (stream === s.id ? " active" : "")}
              onClick={() => setStream(s.id)}
            >
              <span className="board-dot" style={{ background: s.colour }} />
              {s.name} <span className="board-tab-count">{cards.filter((c) => c.streamId === s.id).length}</span>
            </button>
          ))}
        </div>
        <div className="search-bar" style={{ maxWidth: 300 }}>
          <Search size={14} />
          <input
            placeholder="Search cards…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
        </div>
      </div>

      <div className="ops-board">
        {OPS_COLUMN_ORDER.map((col) => (
          <BoardColumn
            key={col}
            col={col}
            cards={byColumn[col] ?? []}
            loading={loading}
            dragging={draggingRef}
            onDragStart={setDraggingRef}
            onDragEnd={() => setDraggingRef(null)}
            onDrop={(ref) => void move(ref, col)}
            onOpen={setOpenRef}
            streamName={streamName}
            streamColour={streamColour}
          />
        ))}
      </div>

      {openRef ? (
        <CardPanel
          cardRef={openRef}
          streams={streams}
          onClose={() => setOpenRef(null)}
          onChanged={(c) => setCards((prev) => prev.map((x) => (x.ref === c.ref ? c : x)))}
          onDeleted={(ref) => {
            setCards((prev) => prev.filter((x) => x.ref !== ref));
            setOpenRef(null);
          }}
        />
      ) : null}

      {composing ? (
        <ComposeModal
          streams={streams}
          defaultStream={stream === "all" ? (streams[0]?.id ?? "orbit") : stream}
          onClose={() => setComposing(false)}
          onCreated={(c) => {
            setCards((prev) => [c, ...prev]);
            setComposing(false);
            toast.push("success", `${c.ref} created.`);
          }}
        />
      ) : null}
    </div>
  );
}

/* ------------------------------------------------------------------ column -- */

function BoardColumn({
  col, cards, loading, dragging, onDragStart, onDragEnd, onDrop, onOpen, streamName, streamColour
}: {
  col: OpsColumn;
  cards: OpsCard[];
  loading: boolean;
  dragging: string | null;
  onDragStart: (ref: string) => void;
  onDragEnd: () => void;
  onDrop: (ref: string) => void;
  onOpen: (ref: string) => void;
  streamName: (id: string) => string;
  streamColour: (id: string) => string;
}) {
  const [over, setOver] = useState(false);
  const canDrop = dragging != null;

  return (
    <section
      className={
        "ops-col" +
        (over && canDrop ? " is-drop-target" : "") +
        (col === "needs_jared" ? " is-attention" : "") +
        (col === "shipped" ? " is-terminal" : "")
      }
      onDragOver={(e) => { if (canDrop) { e.preventDefault(); setOver(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget as Node)) setOver(false); }}
      onDrop={(e) => { e.preventDefault(); setOver(false); if (dragging) onDrop(dragging); }}
    >
      <header className="ops-col-head" title={OPS_COLUMN_HINTS[col]}>
        <span className="ops-col-name">{OPS_COLUMN_LABELS[col]}</span>
        <span className="ops-col-count">{cards.length}</span>
      </header>
      <div className="ops-col-body">
        {loading && cards.length === 0 ? (
          <p className="faint" style={{ padding: "10px 2px" }}>Loading…</p>
        ) : cards.length === 0 ? (
          <p className="faint ops-col-empty">{OPS_COLUMN_HINTS[col]}</p>
        ) : (
          cards.map((c) => (
            <Card
              key={c.ref}
              card={c}
              dragging={dragging === c.ref}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
              onOpen={onOpen}
              streamName={streamName}
              streamColour={streamColour}
            />
          ))
        )}
      </div>
    </section>
  );
}

function Card({
  card, dragging, onDragStart, onDragEnd, onOpen, streamName, streamColour
}: {
  card: OpsCard;
  dragging: boolean;
  onDragStart: (ref: string) => void;
  onDragEnd: () => void;
  onOpen: (ref: string) => void;
  streamName: (id: string) => string;
  streamColour: (id: string) => string;
}) {
  const band = priorityBand(card.priority);
  const claimable = card.col === "ready";
  const unshaped = claimable && (!card.acceptance || card.acceptance.length === 0);

  return (
    <button
      type="button"
      className={"card card-hover ops-card" + (dragging ? " is-dragging" : "")}
      draggable
      onClick={() => onOpen(card.ref)}
      onDragStart={(e) => {
        e.dataTransfer.effectAllowed = "move";
        e.dataTransfer.setData("text/plain", card.ref);
        onDragStart(card.ref);
      }}
      onDragEnd={onDragEnd}
      style={{ borderLeftColor: streamColour(card.streamId) }}
    >
      <div className="ops-card-top">
        <span className="ops-ref">{card.ref}</span>
        {band.label !== "P4" ? <span className={"pill " + band.tone}>{band.label}</span> : null}
      </div>
      <div className="ops-card-title">{card.title}</div>
      <div className="ops-card-foot">
        <span className="ops-stream">{streamName(card.streamId)}</span>
        {card.reviewLaps > 0 ? (
          <span className="ops-laps" title={`${card.reviewLaps} failed review lap${card.reviewLaps === 1 ? "" : "s"}`}>
            ↻{card.reviewLaps}
          </span>
        ) : null}
        {card.prUrl ? <GitBranch size={11} /> : null}
        {card.demandTenants.length > 1 ? (
          <span className="ops-demand" title={`${card.demandTenants.length} tenants asked`}>
            ×{card.demandTenants.length}
          </span>
        ) : null}
        <span className="spacer" />
        <span className="faint">{shortDate(card.updatedAt)}</span>
      </div>
      {unshaped ? (
        <div className="ops-card-warn" title="No acceptance criteria, so nothing can claim it">
          <AlertTriangle size={11} /> No criteria
        </div>
      ) : null}
      {card.claimedBy ? <div className="ops-card-claim">{card.claimedBy}</div> : null}
    </button>
  );
}

/* ------------------------------------------------------------------- panel -- */

function CardPanel({
  cardRef, streams, onClose, onChanged, onDeleted
}: {
  cardRef: string;
  streams: OpsStream[];
  onClose: () => void;
  onChanged: (c: OpsCard) => void;
  onDeleted: (ref: string) => void;
}) {
  const toast = useToast();
  const [card, setCard] = useState<OpsCard | null>(null);
  const [events, setEvents] = useState<OpsEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [draftBody, setDraftBody] = useState("");
  const [newCriterion, setNewCriterion] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getOpsCard(cardRef);
      setCard(res.card);
      setDraftBody(res.card.body);
      setEvents(res.events ?? []);
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not open the card.");
      onClose();
    } finally {
      setLoading(false);
    }
    // deps are [cardRef] only, deliberately: including `toast` and `onClose`
    // re-ran this on every poll and every toast, replacing whatever was being
    // typed in the Detail box with the server copy.
  }, [cardRef]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { void load(); }, [load]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape" && !confirmDelete) onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, confirmDelete]);

  const apply = useCallback((c: OpsCard) => { setCard(c); onChanged(c); }, [onChanged]);

  const save = useCallback(async (changes: Parameters<typeof updateOpsCard>[1]) => {
    if (!card) return;
    setSaving(true);
    try {
      const res = await updateOpsCard(card.ref, changes);
      apply(res.card);
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not save.");
    } finally {
      setSaving(false);
    }
  }, [card, apply, toast]);

  const toggleCriterion = useCallback((idx: number) => {
    if (!card?.acceptance) return;
    const next = card.acceptance.map((a, i) => (i === idx ? { ...a, met: !a.met } : a));
    void save({ acceptance: next });
  }, [card, save]);

  const addCriterion = useCallback(() => {
    const text = newCriterion.trim();
    if (!text || !card) return;
    const next: AcceptanceCriterion[] = [...(card.acceptance ?? []), { criterion: text, met: false }];
    setNewCriterion("");
    void save({ acceptance: next });
  }, [newCriterion, card, save]);

  const removeCriterion = useCallback((idx: number) => {
    if (!card?.acceptance) return;
    void save({ acceptance: card.acceptance.filter((_, i) => i !== idx) });
  }, [card, save]);

  const send = useCallback(async () => {
    const text = comment.trim();
    if (!text || !card) return;
    setComment("");
    try {
      await commentOpsCard(card.ref, text);
      await load();
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not post the comment.");
    }
  }, [comment, card, load, toast]);

  const band = card ? priorityBand(card.priority) : null;

  return (
    <>
      <div className="ops-panel-backdrop" onClick={onClose} />
      <aside className="ops-panel" role="dialog" aria-modal="true">
        <header className="ops-panel-head">
          <div>
            <span className="ops-ref">{cardRef}</span>
            {band && band.label !== "P4" ? <span className={"pill " + band.tone}>{band.label}</span> : null}
            {card ? <span className="pill">{OPS_COLUMN_LABELS[card.col]}</span> : null}
          </div>
          <button className="icon-btn" onClick={onClose} aria-label="Close"><X size={16} /></button>
        </header>

        {loading || !card ? (
          <p className="muted" style={{ padding: 20 }}>Loading…</p>
        ) : (
          <div className="ops-panel-body">
            <input
              className="ops-title-input"
              value={card.title}
              onChange={(e) => setCard({ ...card, title: e.target.value })}
              onBlur={(e) => {
                const next = e.target.value.trim();
                if (next && next !== card.title) void save({ title: next });
                else if (!next) { setCard({ ...card, title: card.title }); toast.push("error", "A card needs a title."); }
              }}
            />

            <div className="ops-meta-grid">
              <label>
                <span className="faint">Stream</span>
                <select value={card.streamId} onChange={(e) => void save({ streamId: e.target.value })}>
                  {streams.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </label>
              <label>
                <span className="faint">Priority</span>
                <select value={String(card.priority)} onChange={(e) => void save({ priority: Number(e.target.value) })}>
                  <option value="10">P0 — a paying tenant cannot work</option>
                  <option value="25">P1 — degraded, workaround exists</option>
                  <option value="40">P2 — multiple tenants, or revenue-linked</option>
                  <option value="60">P3 — single request</option>
                  <option value="80">P4 — internal nice-to-have</option>
                </select>
              </label>
            </div>

            <section className="ops-section">
              <h4>Detail</h4>
              <textarea
                rows={5}
                value={draftBody}
                placeholder="What is actually being asked for, and why."
                onChange={(e) => setDraftBody(e.target.value)}
                onBlur={() => { if (draftBody !== card.body) void save({ body: draftBody }); }}
              />
            </section>

            <section className="ops-section">
              <h4>
                Acceptance criteria
                <span className="faint" style={{ fontWeight: 400, marginLeft: 8 }}>
                  nothing can be claimed without at least one
                </span>
              </h4>
              {(card.acceptance ?? []).length === 0 ? (
                <p className="faint" style={{ margin: "0 0 8px" }}>None yet. This card cannot move past Ready.</p>
              ) : (
                <ul className="ops-criteria">
                  {(card.acceptance ?? []).map((a, i) => (
                    <li key={i}>
                      <button type="button" className="ops-crit-check" onClick={() => toggleCriterion(i)} disabled={saving}>
                        {a.met ? <CheckCircle2 size={15} style={{ color: "var(--success)" }} /> : <Circle size={15} />}
                      </button>
                      <span className={a.met ? "ops-crit-met" : undefined}>{a.criterion}</span>
                      <button type="button" className="icon-btn ops-crit-del" onClick={() => removeCriterion(i)} aria-label="Remove">
                        <X size={12} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
              <div className="row" style={{ gap: 6 }}>
                <input
                  placeholder="Add a criterion…"
                  value={newCriterion}
                  onChange={(e) => setNewCriterion(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCriterion(); } }}
                />
                <button className="btn btn-ghost btn-sm" onClick={addCriterion} disabled={!newCriterion.trim()}>Add</button>
              </div>
            </section>

            {card.testPlan ? (
              <section className="ops-section">
                <h4>Test plan</h4>
                <p className="muted" style={{ whiteSpace: "pre-wrap" }}>{card.testPlan}</p>
              </section>
            ) : null}

            <section className="ops-section">
              <h4>Links</h4>
              <dl className="detail-list">
                <dt>Branch</dt>
                <dd>{card.branch ? <span className="mono">{card.branch}</span> : <span className="faint">none</span>}</dd>
                <dt>Pull request</dt>
                <dd>
                  {card.prUrl ? (
                    <a href={card.prUrl} target="_blank" rel="noreferrer" className="row" style={{ gap: 5 }}>
                      open <ExternalLink size={12} />
                    </a>
                  ) : <span className="faint">none</span>}
                </dd>
                {card.planRef ? (<><dt>Plan</dt><dd className="mono">{card.planRef}</dd></>) : null}
                {card.supportRefs.length ? (<><dt>Support</dt><dd className="mono">{card.supportRefs.join(", ")}</dd></>) : null}
                {card.demandTenants.length ? (
                  <><dt>Asked by</dt><dd>{card.demandTenants.join(", ")}</dd></>
                ) : null}
                <dt>Claimed by</dt>
                <dd>{card.claimedBy ? `${card.claimedBy} · ${relative(card.claimedAt ?? card.updatedAt)}` : <span className="faint">unclaimed</span>}</dd>
                <dt>Created</dt>
                <dd>{fullDate(card.createdAt)} · {card.createdBy}</dd>
              </dl>
            </section>

            <section className="ops-section">
              <h4>Activity</h4>
              <div className="ops-trail">
                {events.length === 0 ? <p className="faint">Nothing yet.</p> : events.slice().reverse().map((e) => (
                  <div key={e.id} className="ops-trail-row">
                    <span className="ops-trail-when">{relative(e.at)}</span>
                    <span className="ops-trail-who">{e.actor}</span>
                    <span className="ops-trail-what">
                      {e.kind === "move" ? `moved ${e.fromCol ?? "?"} → ${e.toCol ?? "?"}` :
                       e.kind === "claim" ? "claimed it" :
                       e.kind === "create" ? "created it" :
                       String((e.detail?.text as string) ?? (e.detail?.note as string) ?? e.kind)}
                    </span>
                  </div>
                ))}
              </div>
              <div className="ops-composer">
                <MessageSquare size={14} />
                <input
                  placeholder="Add a note. An agent reads these as instructions."
                  value={comment}
                  onChange={(e) => setComment(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); void send(); } }}
                />
                <button className="btn btn-ghost btn-sm" onClick={() => void send()} disabled={!comment.trim()}>Post</button>
              </div>
            </section>

            <div className="row between" style={{ marginTop: 4 }}>
              <button className="btn btn-danger btn-sm" onClick={() => setConfirmDelete(true)}>
                <Trash2 size={13} /> Delete card
              </button>
              {saving ? <span className="faint">Saving…</span> : null}
            </div>
          </div>
        )}
      </aside>

      <ConfirmDialog
        open={confirmDelete}
        title="Delete card"
        message={<>This removes <strong>{cardRef}</strong> and its activity trail. It cannot be undone.</>}
        danger
        confirmLabel="Delete"
        onCancel={() => setConfirmDelete(false)}
        onConfirm={async () => {
          // A 409 (a worker holds the lease) used to leave the dialog open with
          // no message, no spinner, and an unhandled rejection in the console.
          try {
            await deleteOpsCard(cardRef);
            setConfirmDelete(false);
            onDeleted(cardRef);
          } catch (err) {
            setConfirmDelete(false);
            toast.push("error", err instanceof Error ? err.message : "Could not delete the card.");
          }
        }}
      />
    </>
  );
}

/* ----------------------------------------------------------------- compose -- */

function ComposeModal({
  streams, defaultStream, onClose, onCreated
}: {
  streams: OpsStream[];
  defaultStream: string;
  onClose: () => void;
  onCreated: (c: OpsCard) => void;
}) {
  const toast = useToast();
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");
  const [streamId, setStreamId] = useState(defaultStream);
  const [priority, setPriority] = useState(60);
  const [busy, setBusy] = useState(false);

  const submit = useCallback(async () => {
    if (!title.trim()) return;
    setBusy(true);
    try {
      const res = await createOpsCard({
        streamId, title: title.trim(), body: body.trim(), col: "inbox", priority, source: "jared"
      });
      onCreated(res.card);
    } catch (err) {
      toast.push("error", err instanceof Error ? err.message : "Could not create the card.");
    } finally {
      setBusy(false);
    }
  }, [title, body, streamId, priority, onCreated, toast]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>New card</h2>
        <p className="muted">Lands in Inbox. Shape it with acceptance criteria before anything can pick it up.</p>
        <div className="form">
          <label>
            Title
            <input autoFocus value={title} onChange={(e) => setTitle(e.target.value)} placeholder="What needs to happen" />
          </label>
          <label>
            Detail
            <textarea rows={4} value={body} onChange={(e) => setBody(e.target.value)} placeholder="Context, links, what you already know" />
          </label>
          <div className="form-grid">
            <label>
              Stream
              <select value={streamId} onChange={(e) => setStreamId(e.target.value)}>
                {streams.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label>
              Priority
              <select value={String(priority)} onChange={(e) => setPriority(Number(e.target.value))}>
                <option value="10">P0 — a paying tenant cannot work</option>
                <option value="25">P1 — degraded, workaround exists</option>
                <option value="40">P2 — multiple tenants, or revenue-linked</option>
                <option value="60">P3 — single request</option>
                <option value="80">P4 — internal nice-to-have</option>
              </select>
            </label>
          </div>
        </div>
        <div className="modal-actions">
          <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={() => void submit()} disabled={!title.trim() || busy}>
            {busy ? "Creating…" : "Create card"}
          </button>
        </div>
      </div>
    </div>
  );
}
